import { Box, Theme, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { Notifications, Add } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { AlertCard } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { ReminderConfigTable, ReminderConfig } from './ReminderConfigTable';
import { ReminderConfigModal } from './ReminderConfigModal';
import { GET_REMINDER_CONFIGS } from './reminder-configs-model.gql';
import { SaveReminderConfigRequest, ReminderConfigResponse } from './reminder-config-api';
import { useSnackbar } from '../../../../../global/config/useSnackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
      display: 'flex',
      justifyContent: 'space-between',
    },
    headingLeft: {
      flex: 1,
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
    addButton: {
      minWidth: '140px',
    },
  })
);

// Define a temporary type for now since GraphQL types aren't generated yet
interface GetReminderConfigs {
  tm: {
    tenant: {
      config: {
        reminderConfigs: ReminderConfig[];
      } | null;
    };
  };
}

const AdminConsoleCommunicationReminders = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ReminderConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useCampfireQuery<GetReminderConfigs, {}>(GET_REMINDER_CONFIGS, {});

  const saveReminderFetch = useCampfireFetch<ReminderConfigResponse>({
    defer: true,
    withAuth: true,
  });

  const deleteReminderFetch = useCampfireFetch<ReminderConfigResponse>({
    defer: true,
    withAuth: true,
  });

  const setConfigItemFetch = useCampfireFetch<{ success: boolean }>({
    defer: true,
    withAuth: true,
  });

  const reminderConfigs = data?.tm?.tenant?.config?.reminderConfigs || [];

  const handleAddReminder = () => {
    setEditingConfig(null);
    setModalOpen(true);
  };

  const handleEditReminder = (config: ReminderConfig) => {
    setEditingConfig(config);
    setModalOpen(true);
  };

  const handleDeleteReminder = async (configId: string) => {
    try {
      await deleteReminderFetch.run({
        method: 'post',
        url: `${apiUrl}/tm/config/reminder-configs/delete`,
        data: { id: configId },
      });
      setSnackbar({
        open: true,
        message: 'Reminder configuration deleted successfully',
        variant: 'success',
      });
      refetch();
    } catch (deleteError) {
      console.error('Error deleting reminder config:', deleteError);
      setSnackbar({
        open: true,
        message: 'Failed to delete reminder configuration',
        variant: 'error',
      });
    }
  };

  const handleSaveReminder = async (configData: SaveReminderConfigRequest) => {
    setSaving(true);
    try {
      await saveReminderFetch.run({
        method: 'post',
        url: `${apiUrl}/tm/config/reminder-configs/save`,
        data: configData,
      });

      // If this is an SMS reminder config, disable legacy SMS system
      const isSmsReminderType =
        configData.reminderType === 'sms-roster-reminder' || configData.reminderType === 'sms-roster-attendance';

      if (isSmsReminderType) {
        try {
          await setConfigItemFetch.run({
            method: 'post',
            url: `${apiUrl}/vm/admin-console/operations/config-item`,
            data: {
              key: 'use-legacy-sms',
              value: false,
            },
          });
        } catch (configError) {
          console.error('Failed to disable legacy SMS, but reminder config was saved:', configError);
          // Don't fail the entire operation if this fails
        }
      }

      setSnackbar({
        open: true,
        message: editingConfig
          ? 'Reminder configuration updated successfully'
          : 'Reminder configuration created successfully',
        variant: 'success',
      });
      setModalOpen(false);
      setEditingConfig(null);
      refetch();
    } catch (saveError) {
      console.error('Error saving reminder config:', saveError);
      setSnackbar({
        open: true,
        message: 'Failed to save reminder configuration',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingConfig(null);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box className={classes.headingLeft}>
          <Typography variant='h2' className={classes.headingTitle}>
            Reminders & Automations
          </Typography>
          <Typography variant={'body2'}>
            {'Manage automated reminders and communication settings for your organisation'}
          </Typography>
        </Box>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleAddReminder}
          startIcon={<Add />}
          className={classes.addButton}
        >
          Add Automation
        </TabletButton>
      </Box>

      <Box className={classes.contentContainer}>
        <Box>
          <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
            <Notifications style={{ fontSize: '14px', paddingRight: '5px' }} /> Automated Reminders & Communications
          </Typography>
          <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', marginBottom: '1rem' }}>
            {
              'Configure automatic reminders, birthday messages, anniversary notifications, and other automated communications for your volunteers.'
            }
          </Typography>

          <AlertCard variant='info' title='How do reminders and automations work?'>
            <Typography className={classes.infoBody}>
              The automated reminders and communications system helps keep your volunteers engaged and informed. You can
              configure different types of automated messages including activity reminders, birthday wishes, anniversary
              celebrations, task notifications, and deadline alerts.
              <br />
              <br />
              Create automation rules below to send timely email notifications to the right people based on your
              organisation&apos;s preferences. You can customise timing and recipients for each type of automation.
            </Typography>
          </AlertCard>

          <ReminderConfigTable
            reminderConfigs={reminderConfigs}
            onEdit={handleEditReminder}
            onDelete={handleDeleteReminder}
            loading={loading}
            error={error}
          />
        </Box>
      </Box>

      <ReminderConfigModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveReminder}
        editingConfig={editingConfig}
        loading={saving}
      />
    </Box>
  );
};

export { AdminConsoleCommunicationReminders };
